import { defineStore } from 'pinia'
import unionBy from 'lodash.unionby'
import { ORGANIZATION_ENDPOINTS } from '@constants/org'
import { USER_ROLES } from '@constants/user'
import { ALL_REGIONS } from '@/constants/region'
import type { RegionServerKeys } from '@/constants/region'
import { client } from '@/services/client'
import { useUser } from '@/store/modules/App/user'
import type { Org, User, FeatureLimit } from '@/types'
import type { SpaceTypeCount } from '@/types/space'
import { isEmpty } from '@utils/checkers'

export type State = {
  org: Org
  usOrg: Org
  caOrg: Org
  apOrg: Org
}

type ParamLoadOrg = { region?: string; showPasswordComplexity?: boolean }

export const useOrganizationStore = defineStore('organization', {
  state: (): State => ({
    org: {} as Org,
    usOrg: {} as Org,
    caOrg: {} as Org,
    apOrg: {} as Org,
  }),
  getters: {
    getOrg: (state: State): Org => state.org,
    getOrgLogo: (state: State): string => state.org?.settings?.logo,
    userHasOrg: (): boolean => {
      return !isEmpty(useUser().getUserInfo?.org)
    },
    isOrgMember: (): boolean => {
      return useUser().getUserInfo?.org_role === USER_ROLES.member
    },
    isOrgOwner: (): boolean => {
      return useUser().getUserInfo?.org_role === USER_ROLES.owner
    },
    isOrgOwnerAdmin: (): boolean => {
      const role = useUser().getUserInfo?.org_role as USER_ROLES
      return [USER_ROLES.admin, USER_ROLES.owner].includes(role)
    },
    getOrgFromRegion:
      (state: State) =>
      (region?: string | null): Org => {
        if (region) {
          return state[`${region}Org`]
        }

        return state.org
      },
    getRestrictedRegions: (state: State): RegionServerKeys[] =>
      state.org.restricted_regions,
    canCreatePrivateSpaces(): boolean {
      if (this.userHasOrg) {
        return !this.org.disable_private_spaces
      }
      return true
    },
    orgFeatureLimits: (state: State): Record<string, FeatureLimit> => {
      if (!state.org.feature_limits) {
        return {}
      }
      return state.org.feature_limits.reduce(
        (acc: Record<string, FeatureLimit>, limit: FeatureLimit) => {
          acc[limit.key] = limit
          return acc
        },
        {},
      )
    },
    spacesTypeCount: (state: State): SpaceTypeCount => {
      return {
        spaceStandardCount:
          state.org.settings?.spaces_info?.standard_spaces_count ?? 0,
        spaceProCount: state.org.settings?.spaces_info?.pro_spaces_count ?? 0,
      }
    },
  },
  actions: {
    setOrg({ org, region }: { org: Org; region: string | null }): void {
      if (region && !isEmpty(org)) {
        this[`${region}Org`] = org
        this.addExternalUsersToOrg(this[`${region}Org`].extended_external_users)
      } else {
        const hasExtendedExternalUsers = !!this.org.extended_external_users
        const extendedExternalUsers = hasExtendedExternalUsers
          ? this.org.extended_external_users
          : org.extended_external_users

        if (!isEmpty(org)) {
          org.extended_external_users = extendedExternalUsers
        }
        this.org = {
          ...org,
        }
      }
    },
    addExternalUsersToOrg(users: User[] = []): void {
      this.org.extended_external_users = unionBy(
        this.org.extended_external_users,
        users,
        'id',
      )
    },
    clearOrg(): void {
      for (const region of ALL_REGIONS) {
        this.setOrg({ org: {} as Org, region })
      }
    },
    async loadOrg({
      region,
      showPasswordComplexity,
    }: ParamLoadOrg = {}): Promise<Org> {
      const userStore = useUser()
      const userHasOrg = !isEmpty(userStore.getUserInfo?.org)
      if (!userHasOrg) {
        return {} as Org
      }
      const endpoint = showPasswordComplexity
        ? `${ORGANIZATION_ENDPOINTS.me}?show_password_complexity=true`
        : ORGANIZATION_ENDPOINTS.me
      const { data } = await client.get(endpoint, {
        region: region ?? null,
      })

      // Merge data to prevent overwriting password complexity info
      if (!showPasswordComplexity && this.org) {
        data.org = { ...this.org, ...data.org }
      }

      this.setOrg({ org: data.org, region: region ?? null })
      return data.org
    },
    async updateOrg(payload: Org | Partial<Org>): Promise<void> {
      await client.put(ORGANIZATION_ENDPOINTS.me, {
        org: payload,
      })

      const showPasswordComplexity = Object.keys(payload).some((key) =>
        key.startsWith('password_rule'),
      )

      await this.loadOrg({ showPasswordComplexity })
    },
    async setOrgSSO(ssoUrl: string): Promise<void> {
      return client.put(ORGANIZATION_ENDPOINTS.SSOSettings, {
        whitelist_url: ssoUrl,
      })
    },
    async updateOrgInvitations(payload: {
      id: string
    }): Promise<{ data: { partner?: User; do_logout?: boolean; org?: Org } }> {
      const { id } = payload
      return await client.put(`${ORGANIZATION_ENDPOINTS.invitations}/${id}`, {
        id,
      })
    },
    async deleteOrgInvitation(payload: { id: number }): Promise<void> {
      const { id } = payload
      return client.delete(`${ORGANIZATION_ENDPOINTS.invitations}/${id}`)
    },
    async bulkOwnershipTransfer(
      payload: { space_id: number; user_id: number }[],
    ): Promise<void> {
      return client.put('/orgs/bulk_ownership_transfer', {
        org: {
          transfer_users: payload,
        },
      })
    },
  },
})

export default useOrganizationStore
